/* eslint-disable max-statements */
export const authenticatedFetch = async <T>(...args: Parameters<typeof fetch>): Promise<T> => {
    const accessToken = '';

    const body = args[1]?.body;
    const headers: RequestInit['headers'] & { 'Content-Type'?: string } = {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...args[1]?.headers,
    };

    // Set content type based on body type
    // Don't set Content-Type for FormData (browser will set it with boundary)
    if (body instanceof FormData) {
        delete headers['Content-Type'];
    } else {
        headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(args[0], {
        ...args[1],
        mode: 'cors',
        cache: 'no-cache',
        headers,
    });

    let responseData: unknown = null;

    try {
        responseData = await response.clone()
            .json() as unknown;
    } catch (error) {
        responseData = await response.text() as unknown;
    }

    return responseData as T;
};

export const get = async <T>(
    url: string, query: object = {}, options: RequestInit = {},
): Promise<T> => {
    let resolvedUrl = url;
    if (Object.keys(query).length > 0) {
        resolvedUrl = `${resolvedUrl}?${new URLSearchParams(query as Record<string, string>)
            .toString()}`;
    }

    return authenticatedFetch<T>(resolvedUrl, {
        ...options,
        method: 'GET',
    });
};

export const post = async <T>(
    url: string, body: object = {}, options: RequestInit = {},
): Promise<T> => authenticatedFetch<T>(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
});
export const postWithFiles = async <T>(
    url: string,
    body: Record<string, string | object> = {},
    files: File[] = [],
    options: RequestInit = {},
): Promise<T> => {
    const formData = new FormData();

    for (const key in body) {
        if (Object.hasOwn(body, key)) {
            const value = body[key];
            if (typeof value === 'object' && value !== null) {
                formData.append(key, JSON.stringify(value));
            } else if (typeof value !== 'undefined') {
                formData.append(key, String(value));
            }
        }
    }

    for (const file of files) {
        formData.append('files', file);
    }

    return authenticatedFetch<T>(url, {
        ...options,
        method: 'POST',
        body: formData,
    });
};

export const put = async <T>(
    url: string, body: object = {}, options: RequestInit = {},
): Promise<T> => authenticatedFetch<T>(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
});

export const patch = async <T>(
    url: string, body: object = {}, options: RequestInit = {},
): Promise<T> => authenticatedFetch<T>(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body),
});

export const del = async <T>(
    url: string, body: object = {}, options: RequestInit = {},
): Promise<T> => authenticatedFetch<T>(url, {
    ...options,
    method: 'DELETE',
    body: JSON.stringify(body),
});
